import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { NotificationService } from '@shared/services/notification.service';
import { IPrinter, PrintService } from '@shared/services/print.service';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { CreatePrinterComponent } from './create-printer/create-printer.component';
import { DeletePrinterComponent } from './delete-printer/delete-printer.component';
import { EditPrinterComponent } from './edit-printer/edit-printer.component';

interface ITablePrinterViewModel {
	alias: string;
	printer: IPrinter;
	description: string;
	encoding: string;
	printerPath: string;
	tags: string;
}

@Component({
	selector: 'app-printer-administration',
	templateUrl: './printer-administration.component.html',
	styleUrl: './printer-administration.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class PrinterAdministrationComponent implements OnDestroy, AfterViewInit {
	private destroy$: Subject<void> = new Subject<void>();
	displayedColumns: string[] = ['alias', 'description', 'encoding', 'printerPath', 'tags', 'menu'];
	dataSource = new MatTableDataSource<ITablePrinterViewModel>();
	showSearch = false;
	isLoading = true;
	isImporting = false;
	private currentSearch = '';
	private searchTerm$ = new Subject<string>();

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	// Viewing the list only requires print:PrinterGet (gated at the menu/route level); create and
	// update are separate, narrower permissions. Create is declared on the SubMenuItem itself -
	// SubMenuHandlerService.evaluateItems() filters favoriteItems/menuItems by item.permissions()
	// whenever items are (re)registered via setFavoriteItem/setMenuItem or accountsSubject$ emits,
	// so this needs no conditional here. Note this is not reactive to UserPermissionsService's
	// permissions changing on its own - same caveat as everywhere else permissions are checked
	// outside of *appHasPermission. Edit is gated in the template instead, via
	// *appHasPermission="'print:PrinterPut'" on the Edit menu item.
	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.administration.printer.create-printer',
			click: () => this.openCreatePrinter(),
			permissions: () => ['print:PrinterPost'],
		},
	];

	constructor(
		public dialog: MatDialog,
		private notificationService: NotificationService,
		private printService: PrintService,
	) {
		this.searchTerm$
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				switchMap((term) => this.fetchPrinters(term)),
				takeUntil(this.destroy$),
			)
			.subscribe((printers) => this.applyPrinters(printers));
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	// Matches the existing formatTagData convention used by docs/templates and
	// calculations/matrices, applied here directly to the already-parsed ITag[] array.
	private formatTags(printer: IPrinter): string {
		return (printer.tags ?? []).map((tag) => `${tag.name}=${tag.value}`).join(', ');
	}

	private getTableViewModelItem(printer: IPrinter): ITablePrinterViewModel {
		return {
			printerPath: printer.printerPath,
			encoding: printer.encoding,
			description: printer.description,
			alias: printer.alias,
			tags: this.formatTags(printer),
			printer,
		};
	}

	private getTableViewModel(printers: IPrinter[]): ITablePrinterViewModel[] {
		return printers.map((printer) => {
			return this.getTableViewModelItem(printer);
		});
	}

	updateList() {
		this.isLoading = true;
		this.fetchPrinters(this.currentSearch)
			.pipe(takeUntil(this.destroy$))
			.subscribe((printers) => this.applyPrinters(printers));
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			this.currentSearch = event.target.value;
			this.isLoading = true;
			this.searchTerm$.next(this.currentSearch);
		}
	}

	private fetchPrinters(searchTerm: string): Observable<IPrinter[]> {
		const alias = searchTerm.trim();
		if (!alias) {
			return this.printService.getPrinters();
		}
		return this.printService.getPrinterByAlias(alias).pipe(
			map((printer) => [printer]),
			catchError(() => of([])),
		);
	}

	private applyPrinters(printers: IPrinter[]): void {
		this.dataSource.data = this.getTableViewModel(printers);
		this.onSort(this.sort);
		this.isLoading = false;
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'alias', start: 'asc', disableClear: true });
		}
	}

	openCreatePrinter() {
		const dialogRef = this.dialog.open(CreatePrinterComponent, {
			disableClose: true,
			width: '650px',
			data: null,
		});
		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	openEditPrinter(printer: IPrinter | null) {
		const dialogRef = this.dialog.open(EditPrinterComponent, {
			disableClose: true,
			width: '650px',
			data: printer,
		});
		dialogRef.componentInstance.edited.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	openDeletePrinter(printer: IPrinter) {
		const dialogRef = this.dialog.open(DeletePrinterComponent, {
			disableClose: true,
			width: '450px',
			data: printer,
		});
		dialogRef.componentInstance.deleted.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.notificationService.notify(['mes.administration.printer.delete.notification.header'], ['mes.administration.printer.delete.notification.content', { name: printer.alias }], 'success');
			this.updateList();
		});
	}
}
